import { type NovelItem, NovelItem_Status } from '@app/_proto/Protos/novels';
import { SectionType } from '@app/_proto/Protos/sections';
import {
    GetSponsorPlansRequest_SortDirection,
    GetSponsorPlansRequest_SortType,
    type SponsorPlanItem,
} from '@app/_proto/Protos/sponsors';
import ChampionCallToAction from '@app/domains/champion/components/ChampionCallToAction';
import ChampionDiscountCallToAction from '@app/domains/champion/components/ChampionDiscountCallToAction';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import useSortedAndAuthChampionPlans from '@app/domains/champion/hooks/useSortedAndAuthChampionPlans';
import { useAuth } from '@app/libs/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

type ChapterCallToActionProps = {
    novel: NovelItem;
    isChapterLocked: boolean;
    onSelectPlan?: (plan: SponsorPlanItem) => void;
};

export default function ChapterCallToAction({ novel, isChapterLocked, onSelectPlan }: ChapterCallToActionProps) {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const {
        activeSubscription: activeChampionSubscription,
        isSuccess: isChampionPlansLoadSuccess,
        isLoading: isActiveSubscriptionLoading,
    } = useChampionSubscriptions({
        novelId: novel?.id,
        enabled: !!user && !!novel?.id,
        keepPreviousData: false,
    });

    const { data: sortedSponsorPlans, isLoading: isChampionPlansLoading } = useSortedAndAuthChampionPlans({
        novelId: novel?.id,
        sortType: GetSponsorPlansRequest_SortType.AdvanceChapters,
        sortDirection: GetSponsorPlansRequest_SortDirection.ASC,
        showModalOnError: false,
    });

    const view = useMemo((): 'champion-discount' | 'champion-novel-completed' | undefined => {
        if (activeChampionSubscription || novel.series?.relatedUserInfo?.isUnlocked || isChapterLocked) {
            return;
        }

        const hasDiscount = sortedSponsorPlans?.find(plan => plan.discountInfo?.discountedPrice !== undefined);
        if (hasDiscount) {
            return 'champion-discount';
        }

        if (novel.status === NovelItem_Status.Finished) {
            return 'champion-novel-completed';
        }
    }, [
        activeChampionSubscription,
        novel.series?.relatedUserInfo?.isUnlocked,
        isChapterLocked,
        sortedSponsorPlans,
        novel.status,
    ]);

    const refetchData = useCallback(() => {
        queryClient.invalidateQueries(['sponsorPlans']);
        queryClient.invalidateQueries(['subscriptions']);
        queryClient.invalidateQueries(['sections', SectionType.NovelCTA]); //Home page subscriptions section
    }, [queryClient]);

    if (isChampionPlansLoading || isActiveSubscriptionLoading || !sortedSponsorPlans || view === undefined) {
        return null;
    }

    return (
        <>
            {view === 'champion-discount' && (
                <ChampionDiscountCallToAction
                    novel={novel}
                    sortedSponsorPlans={sortedSponsorPlans}
                    currentSubscription={activeChampionSubscription ?? null}
                    onSelectPlan={onSelectPlan}
                    refetchData={refetchData}
                />
            )}
            {view === 'champion-novel-completed' && (
                <ChampionCallToAction
                    sortedSponsorPlans={sortedSponsorPlans}
                    currentSubscription={activeChampionSubscription ?? null}
                    onSelectPlan={onSelectPlan}
                />
            )}
        </>
    );
}
